import moment from 'moment'
import { formatInt } from 'uikit/utils/number-formatters'
import { BusinessEntityTypes, type BusinessEntityType, type NodeMetadata } from '../../types'
import { formatMetadataKey, safeStringify } from '../../utils/utils'

export type MetadataKey = keyof NodeMetadata
export type MetadataValueFormatter = (value: unknown) => string | null | undefined

export interface MetadataEntryConfig {
  key: MetadataKey
  label?: string
  format?: MetadataValueFormatter
}

export type MetadataEntry = MetadataKey | MetadataEntryConfig

export interface MetadataSectionConfig {
  label: string
  entries: readonly MetadataEntry[]
}

export interface ResolvedMetadataEntry {
  key: MetadataKey
  label: string
  displayValue: string
}

export const formatDateOnly: MetadataValueFormatter = value => {
  if (typeof value === 'number') {
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined
  }
  if (typeof value !== 'string') return undefined
  const parsed = moment(value, moment.ISO_8601, true)
  if (!parsed.isValid()) return undefined
  return parsed.format('YYYY-MM-DD')
}

export const formatCount: MetadataValueFormatter = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined
  return formatInt(value, { fallback: '' }) ?? undefined
}

export const formatActiveStatus: MetadataValueFormatter = value => {
  if (typeof value !== 'boolean') return undefined
  return value ? 'Active' : 'Inactive'
}

const HOST_SECTION: MetadataSectionConfig = {
  label: 'Host',
  entries: [
    { key: 'first_activity', label: 'Host Since', format: formatDateOnly },
    { key: 'listings_count', label: 'Listings', format: formatCount },
  ],
}

const LISTING_SECTION: MetadataSectionConfig = {
  label: 'Listing',
  entries: [
    { key: 'host_name', label: 'Host' },
    { key: 'owner_name', label: 'Owner' },
    { key: 'license_num', label: 'License #' },
    { key: 'property_type', label: 'Property Type' },
    { key: 'is_active', label: 'Status', format: formatActiveStatus },
    { key: 'reviews_count', label: 'Reviews', format: formatCount },
  ],
}

const REGISTRY_SECTION: MetadataSectionConfig = {
  label: 'Registry',
  entries: [
    { key: 'license_num', label: 'License #' },
    { key: 'license_status', label: 'Status' },
    { key: 'owner_name', label: 'Owner' },
    { key: 'host_name', label: 'Host' },
    { key: 'description', label: 'Description' },
  ],
}

export const NODE_METADATA_SECTIONS: Partial<Record<BusinessEntityType, MetadataSectionConfig>> = {
  [BusinessEntityTypes.LOCATION]: {
    label: 'Location',
    entries: ['address_line1', 'address_line2', 'city', 'state', 'postcode', 'country', 'country_code'],
  },
  [BusinessEntityTypes.AIRBNB_HOST]: HOST_SECTION,
  [BusinessEntityTypes.VRBO_HOST]: HOST_SECTION,
  [BusinessEntityTypes.AIRBNB_LISTING]: LISTING_SECTION,
  [BusinessEntityTypes.VRBO_LISTING]: LISTING_SECTION,
  [BusinessEntityTypes.BOOKING_LISTING]: LISTING_SECTION,
  [BusinessEntityTypes.STR_REGISTRY]: REGISTRY_SECTION,
  [BusinessEntityTypes.STR_REGISTRY_GIS]: REGISTRY_SECTION,
}

export function resolveMetadataEntry(metadata: NodeMetadata, entry: MetadataEntry): ResolvedMetadataEntry | null {
  const config: MetadataEntryConfig = typeof entry === 'string' ? { key: entry } : entry
  const rawValue = metadata[config.key]
  if (rawValue === undefined || rawValue === null || rawValue === '') return null

  let displayValue: string | null | undefined
  if (config.format) {
    displayValue = config.format(rawValue)
  } else if (typeof rawValue === 'string') {
    displayValue = rawValue
  } else if (typeof rawValue === 'number') {
    displayValue = String(rawValue)
  } else {
    displayValue = safeStringify(rawValue)
  }

  if (!displayValue) return null

  return {
    key: config.key,
    label: config.label ?? formatMetadataKey(config.key),
    displayValue,
  }
}

export function getMetadataEntries(metadata: NodeMetadata, entries: readonly MetadataEntry[]): ResolvedMetadataEntry[] {
  return entries.reduce<ResolvedMetadataEntry[]>((acc, entry) => {
    const resolved = resolveMetadataEntry(metadata, entry)
    if (resolved) acc.push(resolved)
    return acc
  }, [])
}
